/**
 * Mega menu behaviour.
 *
 * Desktop: hovering a trigger opens its panel after a short delay, leaving the
 * item closes it after a slightly longer one. The delays stop the panel
 * flickering as the pointer crosses between items, and the close delay gives
 * you time to travel diagonally from the trigger into the panel.
 *
 * Every pointer path is also a click path, so the trigger is a real <button>
 * with aria-expanded. Escape closes and returns focus, Tab out of a panel
 * closes it, and a click outside closes everything.
 *
 * Below the drawer breakpoint the same open state renders as an expanding row
 * instead of a dropdown, so nothing here changes between the two layouts.
 */

const OPEN_DELAY = 90;
const CLOSE_DELAY = 180;

class SpNav extends HTMLElement {
  connectedCallback() {
    this.breakpoint = Number(this.getAttribute('breakpoint')) || 990;
    this.items = Array.from(this.querySelectorAll('[data-nav-item]'));
    this.toggle = this.querySelector('[data-nav-drawer-toggle]');
    this.openTimer = null;
    this.closeTimer = null;

    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-nav-trigger]');
      const panel = item.querySelector('[data-nav-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen(item) ? this.close(item) : this.open(item);
      });

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || !this.isDesktop) return;
        this.clearTimers();
        this.openTimer = setTimeout(() => this.open(item), OPEN_DELAY);
      });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch' || !this.isDesktop) return;
        this.clearTimers();
        this.closeTimer = setTimeout(() => this.close(item), CLOSE_DELAY);
      });

      // Leaving the last focusable thing in the panel should close it, the
      // same as tabbing past any other menu.
      item.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (!item.contains(document.activeElement)) this.close(item);
        });
      });
    });

    if (this.toggle) {
      this.toggle.addEventListener('click', () => {
        const open = this.hasAttribute('drawer-open');
        this.toggleAttribute('drawer-open', !open);
        this.toggle.setAttribute('aria-expanded', String(!open));
        document.documentElement.classList.toggle('sp-nav-drawer-locked', !open);
        if (open) this.closeAll();
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openItem = this.items.find((item) => this.isOpen(item));
      if (openItem) {
        this.close(openItem);
        openItem.querySelector('[data-nav-trigger]')?.focus();
      } else if (this.hasAttribute('drawer-open')) {
        this.toggle?.click();
      }
    });

    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      this.closeAll();
    });

    // A resize across the breakpoint leaves stale state behind, so reset.
    window.addEventListener('resize', () => {
      if (this.isDesktop && this.hasAttribute('drawer-open')) {
        this.removeAttribute('drawer-open');
        this.toggle?.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('sp-nav-drawer-locked');
      }
      this.closeAll();
    });
  }

  get isDesktop() {
    return window.matchMedia(`(min-width: ${this.breakpoint}px)`).matches;
  }

  isOpen(item) {
    return item.hasAttribute('data-open');
  }

  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  open(item) {
    this.clearTimers();
    this.items.forEach((other) => {
      if (other !== item) this.close(other);
    });
    const panel = item.querySelector('[data-nav-panel]');
    if (panel) {
      clearTimeout(panel.spHideTimer);
      panel.hidden = false;
      // Force layout while the panel is still in its closed state, otherwise
      // it goes straight from display:none to open and the transition is lost.
      panel.getBoundingClientRect();
    }
    item.setAttribute('data-open', '');
    item.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'true');
  }

  close(item) {
    if (!this.isOpen(item)) return;
    item.removeAttribute('data-open');
    item.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'false');

    // Keep the panel in the tree while it fades, then take it back out of the
    // accessibility tree so its links are not reachable when closed.
    const panel = item.querySelector('[data-nav-panel]');
    if (!panel) return;
    clearTimeout(panel.spHideTimer);
    panel.spHideTimer = setTimeout(() => {
      if (!this.isOpen(item)) panel.hidden = true;
    }, 220);
  }

  closeAll() {
    this.items.forEach((item) => this.close(item));
  }
}

if (!customElements.get('sp-nav')) {
  customElements.define('sp-nav', SpNav);
}
